import React, { useEffect, useState } from 'react';
import { fetchSupportMedia, formatTime } from './supportApi';
import SupportVoicePlayer from './SupportVoicePlayer';

export default function SupportMessage({ message, apiUrl, token }) {
  const mine = message.senderRole === 'agent';
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(false);
  const isImage = message.kind === 'image';
  const isVoice = message.kind === 'voice';
  const expired = Boolean(message.media?.expired);
  const name = message.media?.fileName || 'Shared attachment';

  useEffect(() => {
    setUrl('');
    setError('');
    setPreview(false);
    if (!isImage || expired || !token) return;
    const controller = new AbortController();
    let objectUrl = '';
    setBusy(true);
    void fetchSupportMedia({ apiUrl, token, messageId: message._id, signal: controller.signal })
      .then(blob => {
        if (!controller.signal.aborted) {
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }
      })
      .catch(e => { if (!controller.signal.aborted) setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setBusy(false); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [message._id, isImage, expired, token, apiUrl]);

  useEffect(() => {
    if (!preview) return;
    const onKey = e => { if (e.key === 'Escape') setPreview(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [preview]);

  function saveBlob(blob, fileName) {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = String(fileName).replace(/[\\/\x00-\x1f]/g, '_') || 'attachment';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
  }

  async function downloadAttachment() {
    if (busy || expired || !token) return;
    setBusy(true);
    setError('');
    try {
      const blob = await fetchSupportMedia({ apiUrl, token, messageId: message._id });
      saveBlob(blob, name);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return <div className={`gp-msg-row ${mine ? 'gp-msg-own' : ''}`}>
    <article className={`gp-bubble ${mine ? 'gp-bubble-own' : ''}`}>
      {message.kind === 'text' && <p className="gp-msg-text">{message.text}</p>}
      {expired ? <div className="gp-expired">⌛ Attachment expired after 30 days</div> : <>
        {isImage && <div>
          {url && <button type="button" className="gp-media-thumb-btn" title="Preview image" onClick={() => setPreview(true)}>
            <img src={url} alt={name} className="gp-inline-img" />
          </button>}
          {!url && <button type="button" className="gp-link" disabled={busy} onClick={() => void downloadAttachment()}>
            {busy ? 'Loading image…' : '🖼 Image unavailable · Save file'}
          </button>}
          {url && <button type="button" className="gp-link" onClick={() => setPreview(true)}>🔍 View image</button>}
        </div>}
        {message.kind === 'file' && <button type="button" className="gp-link" disabled={busy} onClick={() => void downloadAttachment()}>
          📎 {busy ? 'Downloading…' : name}
        </button>}
        {isVoice && <SupportVoicePlayer message={message} token={token} apiUrl={apiUrl} />}
      </>}
      {error && <div role="alert" className="gp-error">{error}</div>}
      <small className="gp-msg-time">{formatTime(message.createdAt)} {mine ? '· Support' : ''}</small>
    </article>
    {preview && url && isImage && <div className="gp-media-overlay" role="presentation" onClick={e => {
      if (e.target === e.currentTarget) setPreview(false);
    }}>
      <section className="gp-media-viewer" role="dialog" aria-modal="true" aria-label="Private image preview">
        <div className="gp-media-viewer-header">
          <strong title={name}>🖼 {name}</strong>
          <button type="button" onClick={() => setPreview(false)} aria-label="Close image viewer">✕</button>
        </div>
        <img src={url} className="gp-media-viewer-image" alt={name} />
        <div className="gp-media-viewer-footer">
          <span>Private support attachment</span>
          <button type="button" disabled={busy} onClick={() => void downloadAttachment()}>
            {busy ? 'Saving…' : '↓ Save image'}
          </button>
        </div>
      </section>
    </div>}
  </div>;
}
